import * as React from 'react'
import { Platform, View } from 'react-native'
import { RootStackParamList, RootTabScreenProps } from '../../navigation/types'
import { SMALL_WIDTH } from '../../constants/GlobalStyle'
import { RouteProp, useRoute } from '@react-navigation/native'
import useMenuData from './Hooks/useMenuData'
import CategoryMenu from './Components/CategoryMenu'
import BrandMenu from './Components/BrandMenu'
import { isRestaurantOpen } from '../../helpers/restaurantHours'
import Colors from '../../constants/Colors'
import OrderBanner from '../../components/Delivery/OrderBanner'
import { useAppSelector } from '../../state/hooks'
import { checkSnooze } from '../../helpers/checkSnooze'
import { useEffect, useState, useMemo } from 'react'
import { AnalyticsEvent, captureEvent } from '../../helpers/analytics'
import { useOrganizationStyles } from '../../hooks/useOrganizationStyles'
import MenuSeo from './seo'
import { Button } from '../../components/DesignSystem'

import NavFooter from '../../components/Footers/NavFooter'
export default function RestaurantScreen({ navigation }: RootTabScreenProps<'restaurant'>) {
	const { loadedData, allDataLoaded, restaurantList, restaurants, filteredCategories } =
		useAppSelector(state => state.data)
	const { isSingleBrand, allowOrdersOutsideOrgHours, localTimezone } = useAppSelector(
		state => state.settings
	)

	const { cart } = useAppSelector(state => state.persisted)
	const cartCount = cart.reduce((count, item) => count + item.qty, 0) //get # of items in cart

	const { logoImage, heroImage } = useAppSelector(state => state.menu)
	const { selectedLocation } = useAppSelector(state => state.persisted)
	const { ctgTokens, storeOpen } = useAppSelector(state => state.menu)

	const route = useRoute<RouteProp<RootStackParamList, 'Menu'>>()
	const routeBySlug = useRoute<RouteProp<RootStackParamList, 'MenuBySlug'>>()

	const [categoryTokens, setCategoryTokens] = useState({})
	const [categoryName, setCategoryName] = useState('')

	const { appBackground } = useOrganizationStyles()

	// Get the initial restaurant ID from various sources
	const initialRestaurantId = useMemo(() => {
		// Priority 1: Route params
		if (route.params?.rId) {
			// If we have the restaurant ID in loadedData, use it directly
			if (loadedData[route.params.rId]) {
				return route.params.rId
			}
			// Otherwise, try to find it in restaurants array
			const restaurant = restaurants.find(r => r.slug === route.params.rId)

			if (restaurant) {
				return restaurant.id
			}
		}

		// Priority 2: Kiosk mode
		if (global.kiosk && selectedLocation?.id) {
			return selectedLocation.id
		}

		// Priority 3: Slug resolution
		if (routeBySlug.params?.slug) {
			const slug = routeBySlug.params.slug
			if (slug.startsWith('category_')) {
				const category = filteredCategories.find(
					c => c.name.toLowerCase() === slug.replace('category_', '')
				)
				if (category) return slug
			} else {
				const restaurant = restaurants.find(r => r.slug === slug)
				if (restaurant) return restaurant.id
			}
		}

		// Priority 4: Default restaurant
		return restaurantList.length > 0 ? restaurantList[0].id : ''
	}, [
		route.params?.rId,
		routeBySlug.params?.slug,
		selectedLocation?.id,
		restaurantList,
		restaurants,
		filteredCategories,
		loadedData,
	])

	// Single source of truth for restaurant ID
	const [rIdToken, setRIdToken] = useState(initialRestaurantId)

	// Update rIdToken when dependencies change
	useEffect(() => {
		if (initialRestaurantId) {
			setRIdToken(initialRestaurantId)
		}
	}, [initialRestaurantId])

	function checkItemAvailability(data) {
		if (data.hours !== null) {
			return isRestaurantOpen(
				data,
				loadedData[global.org].hours ?? null,
				allowOrdersOutsideOrgHours,
				localTimezone
			)
		} else {
			return true
		}
	}

	/**
	 * Load restaurant menu data
	 */
	useMenuData({
		rIdToken,
		navigation,
		checkItemAvailability,
	})

	useEffect(() => {
		if (!rIdToken || !allDataLoaded) return

		const tokenParts = rIdToken.split('_')
		const tokens = {}

		if (tokenParts[0] === 'category') {
			const ctgName = tokenParts[1].replace(/\+|%2B/g, ' ')
			const ctg = filteredCategories.find(cat => cat.name.toLowerCase() === ctgName)

			if (ctg) {
				const items = []
				ctg.items.forEach(item => {
					const menuItem = loadedData[item.id]
					const isAvailable =
						checkItemAvailability(item) &&
						menuItem &&
						!checkSnooze(menuItem.isSnoozed, menuItem.snoozeUntil)

					if (isAvailable) {
						items.push({
							...item,
							isSnoozed: menuItem?.isSnoozed,
							snoozeUntil: menuItem?.snoozeUntil,
						})
					}
				})
				tokens[ctg.name] = items
			}

			setCategoryName(ctgName)
		}

		setCategoryTokens(tokens)
	}, [rIdToken, loadedData, filteredCategories, allDataLoaded])

	// Handle analytics
	useEffect(() => {
		if (!rIdToken || !restaurants.length || !allDataLoaded) return

		if (Platform.OS === 'web') {
			const restaurant = restaurants.find(r => r.id === rIdToken)
			if (!restaurant) return

			const lastViewedMenu = localStorage.getItem('lastViewedMenu')
			if (lastViewedMenu === rIdToken) return

			localStorage.setItem('lastViewedMenu', rIdToken)
			captureEvent(AnalyticsEvent.ViewMenu, {
				restaurantId: rIdToken,
				restaurantName: restaurant.name,
			})
		}
	}, [rIdToken, restaurants, allDataLoaded])

	if (!allDataLoaded || !rIdToken) {
		return <></>
	}

	const tokenParts = rIdToken.split('_')

	const restaurantAddress = () => {
		const location = loadedData[rIdToken]

		if (!location || !location.address) {
			return {
				line1: '',
				line2: '',
			}
		}
		const formattedAddressLine = `${location.address.addressLine1} ${
			location.address.addressLine2 ? location.address.addressLine2 : ''
		}`
		const formattedCityLine = `${location.address.city}, ${location.address.provinceState} ${location.address.postCode}`

		return {
			line1: formattedAddressLine,
			line2: formattedCityLine,
		}
	}
	return (
		<>
			{Platform.OS === 'web' && (
				<MenuSeo seo={loadedData[rIdToken]?.seo} restaurantSlug={loadedData[rIdToken]?.slug} />
			)}
			<View
				style={{
					flex: 1,
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: 'rgba(0,0,0,0.5)',
				}}
			>
				<View
					style={{
						overflow: 'hidden',
						maxWidth: isSingleBrand ? 9999 : 1300,
						width: !SMALL_WIDTH && !isSingleBrand ? '90%' : '100%',
						height: !SMALL_WIDTH && !isSingleBrand ? '90%' : '100%',
						backgroundColor: appBackground,
						borderRadius: !SMALL_WIDTH && !isSingleBrand ? 10 : 0,
					}}
				>
					{isSingleBrand && <OrderBanner navigation={navigation} />}

					{tokenParts[0] === 'category' ? (
						<CategoryMenu
							navigation={navigation}
							ctgTokens={categoryTokens}
							storeOpen={storeOpen}
							isCategory={tokenParts[0] === 'category'}
							logoImageURI={logoImage}
							heroImageURI={heroImage}
							categoryName={categoryName}
						/>
					) : (
						<>
							<BrandMenu
								navigation={navigation}
								ctgTokens={ctgTokens}
								storeOpen={storeOpen}
								logoImageURI={loadedData[rIdToken]?.logoImg ?? ''}
								heroImageURI={loadedData[rIdToken]?.heroImg ?? ''}
								rId={rIdToken}
								address={restaurantAddress()}
								categoryIndex={route.params?.categoryIndex}
							/>
							{cartCount > 0 && (
								<NavFooter
									marginRight={0}
									paddingTop={0}
									navigation={navigation}
									checkoutBar={true}
								/>
							)}
						</>
					)}
				</View>
			</View>
		</>
	)
}
